import { createSlice } from "@reduxjs/toolkit";
import { clearStorage, setStorage } from "../../utils/localStorage";

/**
 * Rehydrate the signed-in user from localStorage on store creation.
 *
 * Without this, `state.user` reset to null on every page refresh — the
 * session token and the `__user__` blob survived in localStorage (and
 * AuthContext read them into its own React state), but Redux never got
 * repopulated. Anything that reads the user FROM REDUX — most importantly
 * `isDemoAccount` via useDemoGuard, the demo banner, and the CRM switcher —
 * therefore went false/blank after a refresh or a deploy, wiping the demo's
 * sample data and making it look like the session had reset. Loading it here
 * keeps the session (demo or paid) intact across reloads; ProtectedRoute
 * still validates the token with the server and clears on a real auth failure.
 */
function loadPersistedUser() {
  try {
    const raw =
      localStorage.getItem("__user__") ||
      localStorage.getItem("__admin_user__") ||
      localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const userSlice = createSlice({
  name: "users",
  initialState: {
    user: loadPersistedUser(),
  },
  reducers: {
    signInSuccess: (state, action) => {
      const user = action.payload;

      // ── User-switch detection ─────────────────────────────────────
      // If a different user is signing in on this browser than the
      // last one (logout-then-login as someone else, demo-then-
      // convert-then-fresh-demo, support taking over a teammate's
      // browser), wipe all the per-user state that would otherwise
      // bleed across users. Without this, the new user inherits
      // things like:
      //   - SetupChecklist's COMPLETED_KEY → activation widget
      //     hidden for someone who never finished setup
      //   - Learning course progress flags → wrong "% complete"
      //   - useIdleLogout's stale ACTIVITY_KEY → /login redirect
      //     loop (the bug we just fixed)
      //   - Onboarding tour seen flags → tour never offered
      //   - Impersonation banner state → wrong banner shown
      // Same trick clearStorage uses: read what we want to keep,
      // wipe everything, restore the kept items. Device-level prefs
      // (cookie consent, PWA prompts) survive via clearStorage's
      // PRESERVE_ON_LOGOUT allowlist.
      try {
        const prevRaw = localStorage.getItem("__user__");
        const prevUser = prevRaw ? JSON.parse(prevRaw) : null;
        const prevId = prevUser?._id || prevUser?.id;
        const nextId = user?._id || user?.id;
        if (prevId && nextId && String(prevId) !== String(nextId)) {
          clearStorage();
        }
      } catch { /* JSON parse fail / no prior user — safe to skip */ }

      state.user = user;
      // save tokens in localstorage
      setStorage("__login_user_token__", user?.token);
      setStorage("__user__", user, "object");
      // Store refresh token for automatic session rotation
      if (user?.refreshToken) {
        setStorage("__refresh_token__", user.refreshToken);
      }
      // Reset the idle-logout activity stamp to "now" on every fresh
      // sign-in. Without this, a brand-new session inherits an old
      // ACTIVITY_KEY from whoever previously used this browser — and
      // useIdleLogout's mount-time check (lines 167-181) fires
      // performLogout immediately if that stale timestamp is older
      // than the 60-min idle threshold. The redirect goes to
      // /login?reason=idle_timeout, the login page sees the new
      // valid token and bounces back to the dashboard, the dashboard
      // re-mounts ProtectedRoute, useIdleLogout reads the same
      // stale key, fires logout again — and you get the
      // "Verifying access" ↔ Sign-in page blinking. Writing a
      // fresh stamp here cuts the loop at the source.
      try {
        localStorage.setItem("__last_user_activity_ms__", String(Date.now()));
      } catch { /* localStorage unavailable — non-fatal */ }
    },
    logoutUser : (state) => {
      state.user = null;
      clearStorage();
      // Callers must follow this with a FULL page reload (not a SPA
      // navigate) so RTK Query caches across ~50 API slices are
      // dropped — otherwise the next user on the same browser would
      // briefly see the previous user's cached client/property data.
    },
    /**
     * Update fields on the current user without the signInSuccess
     * "different user → wipe storage" branch. Used by the demo CRM
     * switcher when role/plan flip in place — same person, same
     * session, just a different vertical to explore.
     */
    updateCurrentUser: (state, action) => {
      if (!state.user) return;
      const next = { ...state.user, ...action.payload };
      state.user = next;
      try { setStorage("__user__", next, "object"); } catch { /* quota */ }
    },
  },
});

export const { signInSuccess, logoutUser, updateCurrentUser } = userSlice.actions;
export default userSlice.reducer;
